import { StatusChip, type ChipTone } from '@/design';
import { idTypeLabel } from '@/domains/auth';
import type { SelfConfirmation } from '../api/participant-contracts';

/**
 * A registry number as a chip — its type, its value and one word for where it stands:
 * `verified`, `denied` or `unverified` — on the person hub's header and its household cards,
 * and on a People row's participants, where a reviewer needs "which numbers, and are they good"
 * before reading any table.
 *
 * **The word is read off the number as sent, in one order.** A denial first, because it is the
 * one that asks for work — an owner said the product holds a number that is not theirs — and
 * survey-api keeps it beside a verification rather than instead of one. Then `verified` (a
 * `verified_at`, however it was stamped), else `unverified`. The owner's own "that is mine" is
 * not a verification and is not shown as one here: the number's row in Identity & numbers says
 * both facts in full.
 *
 * It wraps, where a chip would not: a migrated or invented number can be longer than a phone is
 * wide, and a chip that kept to one line pushed the whole page sideways.
 */

export interface IRegistryNumberChipFacts {
  idType: string;
  value: string;
  verified: boolean;
  /** The hub's identifiers carry it; a People row's do not, and then no denial is claimed. */
  selfConfirmation?: Nullable<SelfConfirmation>;
}

type NumberState = 'denied' | 'verified' | 'unverified';

/** The one table from the word to the design system's tone: a denial is refused, a verified
 *  number settled, an unverified one the plain, usual state. */
const STATE_TONE: Record<NumberState, ChipTone> = {
  denied: 'danger',
  verified: 'success',
  unverified: 'neutral'
};

/** The one word, in the order above: a denial before a verification. */
function stateOf(number: IRegistryNumberChipFacts): NumberState {
  if (number.selfConfirmation === 'denied') return 'denied';
  return number.verified ? 'verified' : 'unverified';
}

export function RegistryNumberChip({
  number,
  compact = false
}: {
  number: IRegistryNumberChipFacts;
  /** A list pane's: the value alone on one line, cut where the name needs the room — the type
   *  and the word said on hover and to a screen reader, the tone still the word's. */
  compact?: boolean;
}) {
  const state = stateOf(number);
  if (compact) {
    const type = idTypeLabel(number.idType);
    return (
      <span title={`${type} ${number.value} · ${state}`} className="inline-flex max-w-full">
        <StatusChip tone={STATE_TONE[state]}>
          <span className="sr-only">{type} </span>
          <span className="font-mono">{number.value}</span>
          <span className="sr-only"> · {state}</span>
        </StatusChip>
      </span>
    );
  }
  return (
    <StatusChip tone={STATE_TONE[state]} wrap>
      {idTypeLabel(number.idType)} <span className="font-mono break-all">{number.value}</span> ·{' '}
      {state}
    </StatusChip>
  );
}
